import { useNavigate, useLocation } from 'react-router-dom'

const HowToHelp = ({ onOpenDonate }) => {
  const navigate = useNavigate()
  const location = useLocation()

  const handleCtaClick = (e, id) => {
    e.preventDefault()
    if (location.pathname !== '/') {
      navigate(`/#${id}`)
      return
    }
    const el = document.getElementById(id)
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' })
    }
  }
  return (
    <section className="relative bg-[#FFF9F4] pt-16 sm:pt-20 lg:pt-24 2xl:pt-32 pb-12 sm:pb-16 2xl:pb-24 overflow-hidden text-center border-t border-slate-100 select-none">
      
      {/* Decorative Dashed Swirls & Sparkles in Background */}
      <div className="absolute top-16 left-12 text-brand-orange-500/20 hidden md:block select-none pointer-events-none">
        <svg className="w-10 h-10 2xl:w-14 2xl:h-14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 3v6M12 15v6M3 12h6M15 12h6" />
        </svg>
      </div>
      <div className="absolute top-36 right-16 text-brand-orange-500/20 hidden md:block select-none pointer-events-none">
        <svg className="w-10 h-10 2xl:w-14 2xl:h-14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 3v6M12 15v6M3 12h6M15 12h6" />
        </svg>
      </div>

      <div className="max-w-7xl 2xl:max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 2xl:px-16 relative z-10">
        
        {/* Title Group with Gold Heart Sketch */}
        <div className="max-w-xl 2xl:max-w-2xl mx-auto mb-12 sm:mb-16 2xl:mb-20 relative">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl 2xl:text-5xl  text-slate-900 tracking-tight inline-flex items-center gap-3">
            How You Can Help Us
            {/* Sketched Heart Outline */}
            <svg className="w-8 h-8 2xl:w-10 2xl:h-10 text-brand-orange-600 fill-none stroke-current shrink-0 transform rotate-12" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
          </h2>
          <p className="text-slate-500 text-xs sm:text-sm md:text-base 2xl:text-lg font-semibold mt-3 max-w-md 2xl:max-w-lg mx-auto leading-relaxed">
            Every little act of kindness creates a big difference in their life.
          </p>
        </div>

        {/* 3 Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12 2xl:gap-16 max-w-5xl 2xl:max-w-6xl mx-auto relative z-10 pt-4 sm:pt-10">

          {/* Card 1: Adopt */}
          <div className="bg-white border border-slate-100 rounded-[32px] 2xl:rounded-[40px] p-5 sm:p-6 2xl:p-7 shadow-lg shadow-slate-900/5 hover:shadow-xl transition-all duration-300 flex flex-col justify-between items-center text-center transform hover:-translate-y-2 relative group">
            
            {/* Peeking Dog atop Card */}
            <div className="absolute -top-[200px] left-1/2 transform -translate-x-1/2 w-60 overflow-hidden pointer-events-none select-none z-20 hidden md:block">
              <img 
                src="/dogpeeking.png" 
                alt="Peeking Dog" 
                className="w-full h-full object-contain object-bottom"
              />
            </div>

            {/* Circular Paw Badge */}
            <div className="absolute top-4 left-4 w-11 h-11 2xl:w-14 2xl:h-14 rounded-full bg-brand-orange-500 border-4 border-white shadow-md flex items-center justify-center text-white z-10">
              <svg className="w-5 h-5 2xl:w-6 2xl:h-6 fill-current" viewBox="0 0 24 24">
                <path d="M12 14c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3zm-5.5-2c-1.38 0-2.5 1.12-2.5 2.5S5.12 17 6.5 17s2.5-1.12 2.5-2.5S7.88 12 6.5 12zm11 0c-1.38 0-2.5 1.12-2.5 2.5s1.12 2.5 2.5 2.5 2.5-1.12 2.5-2.5-1.12-2.5-2.5-2.5zM12 3C8.69 3 6 5.69 6 9c0 1.2.35 2.31.96 3.25" />
              </svg>
            </div>

            <div className="w-full flex flex-col items-center">
              {/* Card Photo */}
              <div className="w-full h-48 sm:h-52 2xl:h-60 rounded-2xl overflow-hidden mt-1 shadow-inner bg-slate-50">
                <img 
                  src="/cta/adopt.png" 
                  alt="Adopt Pets" 
                  className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500"
                />
              </div>

              <h3 className="text-xl 2xl:text-2xl font-display font-semibold text-slate-800 mt-4 leading-none">Adopt</h3>
              <p className="text-xs 2xl:text-sm text-slate-500 mt-2 leading-relaxed max-w-[220px] 2xl:max-w-[260px]">
                Give a loving home to a pet and change two lives forever.
              </p>
            </div>

            <a
              href="#adoption"
              onClick={(e) => handleCtaClick(e, 'adoption')}
              className="w-full mt-4 py-3 2xl:py-3.5 bg-brand-orange-700 hover:bg-brand-orange-850 text-white font-semibold rounded-full text-xs 2xl:text-sm tracking-wider uppercase shadow-md transition-colors text-center block select-none cursor-pointer"
            >
              Adopt Now &gt;
            </a>
          </div>

          {/* Card 2: Donate */}
          <div className="bg-white border border-slate-100 rounded-[32px] 2xl:rounded-[40px] p-5 sm:p-6 2xl:p-7 shadow-lg shadow-slate-900/5 hover:shadow-xl transition-all duration-300 flex flex-col justify-between items-center text-center transform hover:-translate-y-2 relative group">
            
            {/* Circular Green Hand-Heart Badge */}
            <div className="absolute top-4 left-4 w-11 h-11 2xl:w-14 2xl:h-14 rounded-full bg-emerald-600 border-4 border-white shadow-md flex items-center justify-center text-white z-10">
              <svg className="w-5 h-5 2xl:w-6 2xl:h-6 fill-none stroke-current" strokeWidth={2.5} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
              </svg>
            </div>

            <div className="w-full flex flex-col items-center">
              {/* Card Photo */}
              <div className="w-full h-48 sm:h-52 2xl:h-60 rounded-2xl overflow-hidden mt-1 shadow-inner bg-slate-50">
                <img 
                  src="/cta/donate.png" 
                  alt="Donate Feed" 
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>

              <h3 className="text-xl 2xl:text-2xl font-display font-semibold text-slate-800 mt-4 leading-none">Donate</h3>
              <p className="text-xs 2xl:text-sm text-slate-500 mt-2 leading-relaxed max-w-[220px] 2xl:max-w-[260px]">
                Your support helps us provide food, medical care and shelter.
              </p>
            </div>

            <button
              onClick={onOpenDonate}
              className="w-full mt-4 py-3 2xl:py-3.5 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-full text-xs 2xl:text-sm tracking-wider uppercase shadow-md transition-colors text-center block select-none cursor-pointer outline-none"
            >
              Donate Now &gt;
            </button>
          </div>

          {/* Card 3: Volunteer */}
          <div className="bg-white border border-slate-100 rounded-[32px] 2xl:rounded-[40px] p-5 sm:p-6 2xl:p-7 shadow-lg shadow-slate-900/5 hover:shadow-xl transition-all duration-300 flex flex-col justify-between items-center text-center transform hover:-translate-y-2 relative group">
            
            {/* Peeking Cat atop Card */}
            <div className="absolute -top-[200px] left-1/2 transform -translate-x-1/2 w-60 overflow-hidden pointer-events-none select-none z-20 hidden md:block">
              <img 
                src="/catpeeking.png" 
                alt="Peeking Cat" 
                className="w-full h-full object-contain object-bottom"
              />
            </div>

            {/* Circular Blue People-Heart Badge */}
            <div className="absolute top-4 left-4 w-11 h-11 2xl:w-14 2xl:h-14 rounded-full bg-blue-600 border-4 border-white shadow-md flex items-center justify-center text-white z-10">
              <svg className="w-5 h-5 2xl:w-6 2xl:h-6 fill-current" viewBox="0 0 24 24">
                <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 8 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" />
              </svg>
            </div>

            <div className="w-full flex flex-col items-center">
              {/* Card Photo */}
              <div className="w-full h-48 sm:h-52 2xl:h-60 rounded-2xl overflow-hidden mt-1 shadow-inner bg-slate-50">
                <img 
                  src="/cta/volunteer.png" 
                  alt="Volunteer Care" 
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>

              <h3 className="text-xl 2xl:text-2xl font-display font-semibold text-slate-800 mt-4 leading-none">Volunteer</h3>
              <p className="text-xs 2xl:text-sm text-slate-500 mt-2 leading-relaxed max-w-[220px] 2xl:max-w-[260px]">
                Join our team and be the voice for those who can't speak.
              </p>
            </div>

            <a
              href="#volunteer"
              onClick={(e) => handleCtaClick(e, 'volunteer')}
              className="w-full mt-4 py-3 2xl:py-3.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-full text-xs 2xl:text-sm tracking-wider uppercase shadow-md transition-colors text-center block select-none cursor-pointer"
            >
              Join Us &gt;
            </a>
          </div>

        </div>

      </div>
      
    </section>
  )
}

export default HowToHelp
